import { RefObject, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { CustomPop, IncludeLabel, IncludeRow, Pill } from '@components/course/taskEstimator/TaskEstimator.styles';
import { MiniText } from '@utils/typography';
import './estimatePopup.css';

/**
 * The estimate popup's own look and behaviour, shared by the sidebar's task cards (PileEstimateControl) and the Task
 * Estimator's task rows: a card outlined and glowing in the theme colour that springs out of its button and shrinks
 * back into it once something is picked.
 */

// How long the popup takes to shrink back into its button once something is picked.
export const POP_OUT_MS = 150;

// The popup only shows once it has been placed, so a field focused as it is first drawn is not focused at all: the
// focus is tried again each frame until it takes, for about two thirds of a second at most (the first opening can
// take a few frames longer).
const FOCUS_TRIES = 40;

// The time or pages field takes the cursor as the popup opens, so a student can type at once, or still click a preset.
export const useFocusFieldOnOpen = (open: boolean, panelRef: RefObject<HTMLElement>) => {
  useEffect(() => {
    if (!open) return;
    let frame = 0;
    let tries = 0;
    const focusField = () => {
      const field = panelRef.current?.querySelector('input');
      if (field && document.activeElement !== field) {
        field.focus();
        field.select();
      }
      if ((!field || document.activeElement !== field) && ++tries < FOCUS_TRIES) frame = requestAnimationFrame(focusField);
    };
    frame = requestAnimationFrame(focusField);
    return () => cancelAnimationFrame(frame);
  }, [open, panelRef]);
};

// Inside a popup the panel has to belong to it: both the day popup and the deadline-flag popup close on any click
// outside their own DOM, and one appended to the root counts as outside; so does the Task Estimator's dialog. Elsewhere
// it still goes to the root, which is what keeps the pile's virtualised list from clipping it.
export const estimatePopupAppendTo = (ref: Element) =>
  ref.closest('.popup-content') || ref.closest('.tippy-box') || document.getElementById('root') || document.body;

// Springs out of the button it opens from (Petr, 2026-10-08: "more visible and fun"), above it or, with no room, below.
const popIn = keyframes`
  from {
    transform: scale(0.6);
    opacity: 0;
  }
  60% {
    transform: scale(1.04);
    opacity: 1;
  }
  82% {
    transform: scale(0.99);
  }
  to {
    transform: none;
  }
`;

const popOut = keyframes`
  to {
    transform: scale(0.7);
    opacity: 0;
  }
`;

// A course colour the popup can take, when it is a plain six-digit hex the glow's alpha can be added to.
const accentOf = ({ $color, theme }: { $color?: string; theme: any }) =>
  $color && /^#[0-9a-f]{6}$/i.test($color) ? $color : theme.primary;

// Its own card, outlined and glowing in the theme colour, or in the task's course colour where it opens from a sidebar
// task card (Petr, 2026-10-08; the Task Estimator's stays purple), since the tooltip box under it goes clear
// (estimatePopup.css). Presets lift as they are pointed at.
export const EstimatePopupPanel = styled.div<{ $closing?: boolean; $color?: string }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.background};
  border: 2px solid ${accentOf};
  border-radius: 10px;
  box-shadow: 0 10px 28px ${props => accentOf(props)}47, 0 2px 6px ${({ theme }) => theme.boxShadow};
  transform-origin: 100% 100%;
  animation: ${popIn} 340ms cubic-bezier(0.3, 0.7, 0.4, 1);

  ${({ $closing }) =>
    $closing &&
    css`
      animation: ${popOut} ${POP_OUT_MS}ms ease-in forwards;
    `}

  .tippy-box[data-placement^='bottom'] & {
    transform-origin: 100% 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${Pill} {
    transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
  }

  /* Written without :not(), which the formatter splits from its parenthesis and so breaks the rule. */
  ${Pill}:hover:enabled {
    transform: translateY(-2px);
    border-color: ${accentOf};
    box-shadow: 0 3px 8px ${props => accentOf(props)}40;
  }

  /* The presets stay on one row, so the group gets the width it needs. */
  > div {
    flex-wrap: nowrap;
  }

  /* The time field and its Apply sit at the right, under the presets' end and where the popup opens from. */
  ${CustomPop} {
    justify-content: flex-end;
  }
`;

export const EstimatePopupLabel = styled(MiniText)`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.textStrongColor};
`;

// A tick box under a popup's question, on one line: "Also re-estimate the 39 unfinished tasks" (a category's time),
// "Also for 12 other Homework" (a first estimate).
export const PopupCheckRow = styled(IncludeRow)`
  margin-top: 0;
  gap: 6px;
  white-space: nowrap;
`;

export const PopupCheckLabel = styled(IncludeLabel)`
  font-size: 12.5px;
`;
